import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

import AttendanceCard from "./AttendanceCard";
import MonthlyStats from "./MonthlyStats";
import CalendarView from "./CalendarView";
import RecentActivity from "./RecentActivity";
import CompanyAttendance from "./CompanyAttendance";
import AttendanceAnalytics from "./AttendanceAnalytics";
import RegularisationModal from "./RegularisationModal";
import RegularisationRequests from "./RegularisationRequests";
import { API_URL } from '../../api/api';
import { UserCheck, Users, BarChart3, FileText, Clock, Filter, FilePenLine } from "lucide-react";
import { T, PageHeader, Tabs, Select, SecondaryButton } from "../../components/em/ui";
import { hasPermission } from "../../utils/permissionUtils";
import { localMonthStr } from "../../utils/localDate";
import "./em-attendance.css";

import { toast } from "sonner";
/* Summary period filter. Values are the `period` the API resolves against a
   real calendar (backend services/timezone.js) — the client never computes the
   date range itself, so the label and the numbers can't disagree.

   "Monthly" is the one option tied to the calendar below it: it summarises
   whichever month the grid is showing, not always the current one. */
const PERIOD_OPTIONS = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Yearly" },
];

/* ---------- Main Component ---------- */
const Attendance = () => {
  const userData = JSON.parse(localStorage.getItem("userdata") || "{}");
  const token = localStorage.getItem("token");

  const userId = userData.id;
  // Role is stored mixed-case (e.g. "Admin"). Normalise once for comparisons.
  const role = String(
    userData.role || localStorage.getItem("role") || ""
  ).toLowerCase();

  const [activeTab, setActiveTab] = useState("my");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True only while a mark-in / mark-out is in flight, so the button can say
  // so immediately. Mark In waits on the browser for a location fix before it
  // can even send, which previously looked like a dead click.
  const [marking, setMarking] = useState(false);
  // Whether the page has ever finished a load. `loading` blanks the entire tab
  // for a "Loading…" line, which is right on first paint and wrong after that:
  // every mark-in/out called the same fetch, so the card, KPIs, calendar and
  // activity list all unmounted and came back — the "page reloaded" flash.
  // Refreshes now repaint in place.
  const loadedOnceRef = useRef(false);

  const [todayRec, setTodayRec] = useState(null);
  const [stats, setStats] = useState({});
  const [calendarData, setCalendarData] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showRegularise, setShowRegularise] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod] = useState("month");
  // The month the calendar is showing, 'YYYY-MM'. It drives the calendar fetch
  // — the grid can only paint a month that was actually requested — and, while
  // the filter is on "Monthly", the summary window too, so paging the calendar
  // back to August doesn't leave the KPI cards reporting September.
  const [calMonth, setCalMonth] = useState(localMonthStr());
  // Bumped after a successful submit to make the regularisation list refetch.
  const [regularisationRefresh, setRegularisationRefresh] = useState(0);

  const apiBase = `${API_URL}/api/attendance`;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Centralised auth-error handler — bounce to login on 401.
  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.assign("/login");
      return true;
    }
    return false;
  };

  // Today's card + the Recent Activity tail. Neither depends on the period
  // filter or the calendar month, so paging the calendar must not refetch them
  // — and must not put the page back into its full-page loading state.
  const fetchMyAttendance = useCallback(async () => {
    if (!userId) return;
    // Only the very first load may blank the tab — after that there is real
    // content on screen and replacing it with a spinner is strictly worse
    // than letting the numbers update underneath the user.
    const isFirstLoad = !loadedOnceRef.current;
    if (isFirstLoad) setLoading(true);
    setError(null);
    try {
      const [todayRes, recentRes] = await Promise.all([
        axios.get(`${apiBase}/today/${userId}`, authHeader),
        axios.get(`${apiBase}/recent/${userId}`, authHeader),
      ]);
      setTodayRec(todayRes.data || null);
      setRecent(recentRes.data || []);
      loadedOnceRef.current = true;
    } catch (err) {
      console.error("Attendance fetch error", err);
      if (!handleAuthError(err)) {
        setError(
          err?.response?.data?.error || "Could not load attendance data."
        );
      }
    } finally {
      if (isFirstLoad) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // KPI summary + the calendar grid. Both are windowed, and they must be
  // windowed the SAME way or the page contradicts itself: on "Monthly" the
  // window is whichever month the calendar is showing, so the cards describe
  // the grid beside them. Week/quarter/year are anchored on today by the API
  // and the calendar keeps its own position.
  const fetchSummary = useCallback(async () => {
    if (!userId) return;
    try {
      const [statsRes, calRes] = await Promise.all([
        axios.get(`${apiBase}/monthly/${userId}`, {
          ...authHeader,
          params: period === "month" ? { month: calMonth } : { period },
        }),
        // Without an explicit month this returns the CURRENT month, so every
        // other month the calendar paged to came back empty.
        axios.get(`${apiBase}/calendar/${userId}`, {
          ...authHeader,
          params: { month: calMonth },
        }),
      ]);
      setStats(statsRes.data || {});
      setCalendarData(calRes.data || []);
    } catch (err) {
      console.error("Attendance summary fetch error", err);
      if (!handleAuthError(err)) {
        setError(
          err?.response?.data?.error || "Could not load attendance data."
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, period, calMonth]);

  // Both reloads, for after a mark-in / mark-out. Returns a promise so a
  // caller that shows a pending state (the card's break buttons) can hold it
  // until the new numbers are actually in — `await refreshAll()` used to
  // resolve instantly, dropping the spinner before the data landed.
  const refreshAll = useCallback(
    () => Promise.all([fetchMyAttendance(), fetchSummary()]),
    [fetchMyAttendance, fetchSummary]
  );

  useEffect(() => {
    if (!userId || !token) {
      window.location.href = "/login";
      return;
    }
    if (activeTab === "my") fetchMyAttendance();
  }, [fetchMyAttendance, activeTab, userId, token]);

  useEffect(() => {
    if (!userId || !token) return;
    if (activeTab === "my") fetchSummary();
  }, [fetchSummary, activeTab, userId, token]);

  // Best-effort geolocation. We request coords so the backend can flag
  // off-site mark-ins (soft validation — never blocks). Failures are silent;
  // the request still goes through without lat/lng, and the backend will flag
  // it as `remote_location` only if the admin enabled location validation.
  const tryGetGeolocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      let done = false;
      const finish = (val) => {
        if (done) return;
        done = true;
        resolve(val);
      };
      // Hard cap so a slow/silent geolocation prompt doesn't stall mark-in.
      // This sits in FRONT of the request — nothing is sent until it settles —
      // so it is the single biggest contributor to how long the button takes
      // to do anything. Coords are advisory (the server never blocks on them),
      // which is why giving up early is the right trade.
      setTimeout(() => finish({}), 2500);
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          finish({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => finish({}),
        // A five-minute-old fix is still well inside any office radius, and
        // accepting one makes every mark-in after the first return instantly
        // from cache instead of waiting on a fresh GPS lock.
        { enableHighAccuracy: false, timeout: 2500, maximumAge: 300000 }
      );
    });

  // Mark-in and mark-out both answer with the updated attendance row, so the
  // card can switch state the moment the write lands. The rest of the tab
  // (KPIs, calendar, activity) refreshes behind that rather than gating it —
  // waiting on four requests before showing anything was most of the lag.
  const applyMarkResult = (record) => {
    if (record) setTodayRec(record);
    refreshAll();
  };

  const handleMarkIn = async () => {
    if (marking) return; // a second click would open a duplicate mark-in
    setMarking(true);
    try {
      const geo = await tryGetGeolocation();
      const res = await axios.post(
        `${apiBase}/mark-in`,
        { user_id: userId, ...geo },
        authHeader
      );
      applyMarkResult(res.data);
      // Server may return a soft-validation warning (off-site / off-network).
      if (res.data?.warning) {
        toast.error(res.data.warning);
      }
    } catch (err) {
      // 409 = already marked in/out — the server sends the row it found, so
      // the card can correct itself without another round trip.
      if (err?.response?.status === 409) {
        applyMarkResult(err.response.data?.record);
        toast.error(err.response.data?.error || "Already marked.");
        return;
      }
      if (!handleAuthError(err)) {
        toast.error(err?.response?.data?.error || "Could not mark in.");
      }
    } finally {
      setMarking(false);
    }
  };

  const handleMarkOut = async () => {
    if (marking) return;
    setMarking(true);
    try {
      const res = await axios.post(
        `${apiBase}/mark-out`,
        { user_id: userId },
        authHeader
      );
      applyMarkResult(res.data);
    } catch (err) {
      if (!handleAuthError(err)) {
        toast.error(err?.response?.data?.error || "Could not mark out.");
      }
    } finally {
      setMarking(false);
    }
  };

  const isAdminOrHR = role === "admin" || role === "hr";
  // Company-wide attendance authority. A custom role granted
  // `manage_attendance` ("View company attendance and approve
  // regularisations") counts too — such a role arrives here as plain
  // "member", so the role check alone would silently ignore the grant.
  // Mirrors the server (checkRoleOrPermission on /company, and
  // canManageAttendance in attendanceController).
  const canManageAttendance = isAdminOrHR || hasPermission("manage_attendance");
  // Everyone can submit regularisations now: members for themselves,
  // admin/HR for any employee (Q2=a + Q4=a).
  const canRegularise = role === "member" || isAdminOrHR;

  // Admin and HR both need the employee list to pick "on behalf of".
  useEffect(() => {
    if (!isAdminOrHR) return;
    axios
      .get(`${API_URL}/api/settings/shifts/team-members`, authHeader)
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrHR]);

  /* ---------- Render ---------- */
  const TABS = [
    { key: "my", label: "My Attendance", icon: UserCheck },
    ...(canManageAttendance ? [{ key: "company", label: "Company Attendance", icon: Users }] : []),
    ...(isAdminOrHR ? [{ key: "analytics", label: "Analytics Dashboard", icon: BarChart3 }] : []),
    { key: "regularisation", label: "Regularisation", icon: FileText },
  ];

  return (
    <div className="attendance-page" style={{ maxWidth: 1440, margin: "0 auto", padding: "0 32px 48px" }}>
      <PageHeader icon={Clock} title="Attendance" subtitle="Track, manage and analyse team attendance" />

      <div style={{ marginBottom: 20 }}>
        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
      </div>

      {/* Content */}
      {error && (
        <div
          role="alert"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#b91c1c",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ⚠ {error}
        </div>
      )}
      {loading && activeTab === "my" ? (
        <p>Loading attendance data…</p>
      ) : (
        <>
          {/* MY ATTENDANCE */}
          {activeTab === "my" && (
            <>
              <AttendanceCard
                todayRec={todayRec}
                onMarkIn={handleMarkIn}
                onMarkOut={handleMarkOut}
                marking={marking}
                refresh={refreshAll}
              />

              {/* Summary toolbar — sits above the KPI cards it controls, so the
                  period you pick reads left-to-right into the numbers below. */}
              <div className="attd-summary-bar">
                <div className="attd-period">
                  <Filter size={13} color={T.fg5} aria-hidden="true" />
                  {/* backgroundColor, not the `background` shorthand — the
                      shorthand would wipe Select's chevron background-image. */}
                  <Select
                    aria-label="Summary period"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    disabled={loading}
                    style={{ width: "auto", border: 0, backgroundColor: "transparent", padding: "8px 28px 8px 6px", font: `600 13px/1.4 ${T.font}` }}
                  >
                    {PERIOD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </div>

                {canRegularise && (
                  <SecondaryButton
                    icon={FilePenLine}
                    onClick={() => setShowRegularise(true)}
                    style={{ color: T.orange, borderColor: T.orange, font: `600 13px ${T.font}` }}
                  >
                    Regularise Attendance
                  </SecondaryButton>
                )}
              </div>

              <MonthlyStats
                stats={{
                  presentDays: stats.presentDays,
                  halfDays: stats.halfDays,
                  lateDays: stats.lateDays,
                  absentDays: stats.absentDays,
                  leaveDays: stats.leaveDays,
                  otHours: stats.otHours,
                  totalHours: stats.totalHours,
                  avgHours: stats.avgHours,
                  totalBreakHours: stats.totalBreakHours,
                }}
              />

              <div className="attendance-bottom">
                <div className="calendar-section">
                  <CalendarView
                    data={calendarData}
                    month={calMonth}
                    onMonthChange={setCalMonth}
                  />
                </div>

                <div className="activity-section">
                  <RecentActivity logs={recent} />
                </div>
              </div>
            </>
          )}

          {/* COMPANY ATTENDANCE */}
          {canManageAttendance && activeTab === "company" && (
            <CompanyAttendance />
          )}

          {/* ANALYTICS */}
          {isAdminOrHR && activeTab === "analytics" && (
            <AttendanceAnalytics />
          )}

          {/* REGULARISATION */}
          {activeTab === "regularisation" && (
            <RegularisationRequests
              canDecide={canManageAttendance}
              onSubmitNew={canRegularise ? () => setShowRegularise(true) : null}
              refreshKey={regularisationRefresh}
            />
          )}
        </>
      )}

      {/* Regularisation submit modal */}
      {showRegularise && (
        <RegularisationModal
          employees={employees}
          onClose={() => setShowRegularise(false)}
          onSuccess={() => {
            setShowRegularise(false);
            // The list does NOT reload on its own — its fetch effect only runs
            // on mount, so submitting from the Regularisation tab (where the
            // component is already mounted) left the new row invisible until a
            // page reload. Bumping this key re-runs that fetch.
            setRegularisationRefresh((n) => n + 1);
            // No attendance refetch: a pending request doesn't change any
            // attendance row. Approving one does, and that path already reloads.
          }}
        />
      )}
    </div>
  );
};

export default Attendance;
